import { Link, useSearchParams } from 'react-router-dom';
import { codeLabel } from '../../utils/codeLabel';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { operationalQueryPolicy, safePagePlaceholder } from '../../api/queryPolicies';
import { getDocuments, verifyDocument } from '../../api/endpoints/admin';
import type { DocumentStatus, AdminDocumentResponse } from '../../api/types';
import { DataTable } from '../../components/ui/DataTable';
import { Pagination } from '../../components/ui/Pagination';
import { RefreshButton } from '../../components/ui/RefreshButton';
import { StatusPill } from '../../components/ui/StatusPill';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { useMutationHook } from '../../hooks/useMutationHook';
import { actionFailureMessage } from '../../api/actionFailure';
import { documentStatusColor } from '../../components/domain/statusColor';
import { documentSubject } from './documentSubject';
import { DialogHelp } from '../../help/DialogHelp';
import styles from './DocumentsView.module.css';

const STATUS_OPTS: { label: string; value: DocumentStatus | '' }[] = [
  { label: 'All', value: '' },
  { label: 'Pending', value: 'PENDING' },
  { label: 'Uploaded', value: 'UPLOADED' },
  { label: 'Verified', value: 'VERIFIED' },
  { label: 'Rejected', value: 'REJECTED' },
];

function fmtDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function fmtSize(bytes: number | null) {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const VEHICLE_DOC_NOUN: Record<string, string> = {
  VEHICLE_INSURANCE: 'insurance',
  VEHICLE_FITNESS: 'fitness',
};

/** The chip to start on: `?status=` when it names a chip (deep link from the dashboard), else Uploaded. */
function initialStatus(param: string | null): DocumentStatus | '' {
  const match = STATUS_OPTS.find((o) => o.value !== '' && o.value === param);
  return match ? match.value : 'UPLOADED';
}

/** A vehicle renewal waits as PENDING (rhino-api R33); an uploaded one is reviewable too. */
function isVehicleReviewable(d: AdminDocumentResponse) {
  return d.status === 'PENDING' || d.status === 'UPLOADED';
}

function fmtDocType(type: string) {
  return codeLabel('documentType', type);
}

export function DocumentsView() {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<DocumentStatus | ''>(() => initialStatus(searchParams.get('status')));
  const [selectedDoc, setSelectedDoc] = useState<AdminDocumentResponse | null>(null);
  const [isApproveOpen, setIsApproveOpen] = useState(false);
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  const verifyDocMutation = useMutationHook(
    ({ documentId, approve, reason }: { documentId: number; approve: boolean; reason?: string }) =>
      verifyDocument(documentId, approve, reason),
    {
      successMessage: (data, { approve }) =>
        approve
          ? t('documents.decisionToast.verified', { id: data.id })
          : t('documents.decisionToast.rejected', { id: data.id }),
      invalidateKeys: [
        ['admin', 'documents'],
        ['admin', 'dashboard'],
        ['admin', 'shipments'],
        ['payments'],
        ['admin','work'],
        ['admin','scorecard'],
      ],
      errorMessage: actionFailureMessage,
      onSuccess: () => {
        setSelectedDoc(null);
        setIsApproveOpen(false);
        setIsRejectOpen(false);
      },
      onError: (err) => setDialogError(actionFailureMessage(err)),
    },
  );

  const queryKey = ['admin', 'documents', page, status] as const;
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey,
    queryFn: () =>
      getDocuments({
        page,
        size: 25,
        status: status || undefined,
      }),
    ...operationalQueryPolicy,
    placeholderData: safePagePlaceholder(queryKey, 2),
  });

  const urlWithoutQuery = selectedDoc ? selectedDoc.downloadUrl.split('?')[0].toLowerCase() : '';
  const isImage = selectedDoc
    ? urlWithoutQuery.endsWith('.png') ||
      urlWithoutQuery.endsWith('.jpg') ||
      urlWithoutQuery.endsWith('.jpeg') ||
      urlWithoutQuery.endsWith('.webp') ||
      urlWithoutQuery.endsWith('.gif')
    : false;
  const isPdf = selectedDoc ? urlWithoutQuery.endsWith('.pdf') : false;

  return (
    <div className={styles.page}>
      {/* Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.filters}>
          {STATUS_OPTS.map((opt) => (
            <button
              key={opt.value}
              className={status === opt.value ? `${styles.chip} ${styles.chipActive}` : styles.chip}
              onClick={() => {
                setStatus(opt.value);
                setPage(0);
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <RefreshButton
          queryKeys={[
            ['admin', 'documents'],
            ['admin', 'dashboard'],
        ['admin', 'shipments'],
        ['payments'],
        ['admin','work'],
        ['admin','scorecard'],
          ]}
        />
      </div>

      <DataTable
        loading={isLoading}
        error={isError ? 'We couldn’t load documents' : undefined}
        onRetry={() => refetch()}
        keyFn={(d) => d.id}
        rows={data?.items ?? []}
        empty="No documents found"
        columns={[
          {
            key: 'id',
            header: 'ID',
            width: '64px',
            render: (d) => <span className={styles.mono}>#{d.id}</span>,
          },
          {
            key: 'shipmentId',
            header: 'Shipment / vehicle',
            width: '100px',
            render: (d) => {
              const subject = documentSubject(d);
              if (subject.kind === 'shipment') {
                return (
                  <Link to={`/shipments/${subject.shipmentId}?tab=documents&documentId=${d.id}`}>
                    {t('documents.documentsView.reviewShipment', { id: subject.shipmentId })}
                  </Link>
                );
              }
              return subject.kind === 'vehicle' ? subject.label : '—';
            },
          },
          { key: 'type', header: 'Type', width: '130px', render: (d) => fmtDocType(d.type) },
          { key: 'size', header: 'Size', width: '100px', render: (d) => fmtSize(d.fileSize) },
          { key: 'uploadedBy', header: 'Uploaded By', render: (d) => d.uploadedByName || '—' },
          {
            key: 'uploadedAt',
            header: 'Uploaded At',
            width: '120px',
            render: (d) => fmtDate(d.uploadedAt),
          },
          {
            key: 'status',
            header: 'Status',
            width: '120px',
            render: (d) => {
              const c = documentStatusColor(d.status);
              return <StatusPill bg={c.bg} text={c.text} label={codeLabel('documentStatus', d.status)} />;
            },
          },
          {
            key: 'action',
            header: 'Action',
            width: '180px',
            render: (d) => (
              <div className={styles.actionCell}>
                <a
                  href={d.downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.downloadLink}
                >
                  {t('documents.documentsView.download')}
                </a>
                {d.shipmentId != null
                  ? d.status === 'UPLOADED' && (
                      <Link className={styles.reviewBtn} to={`/shipments/${d.shipmentId}?tab=documents&documentId=${d.id}`}>{t('documents.documentsView.reviewInShipment')}</Link>
                    )
                  : isVehicleReviewable(d) && (
                      <button className={styles.reviewBtn} onClick={() => setSelectedDoc(d)}>{t('documents.documentsView.review')}</button>
                    )}
              </div>
            ),
          },
        ]}
      />

      {data && (
        <Pagination
          page={data.page}
          totalPages={data.totalPages}
          totalItems={data.totalItems}
          size={data.size}
          onPage={setPage}
        />
      )}

      {/* Document Review Modal */}
      {selectedDoc && (
        <div className={styles.modalBackdrop} onClick={() => setSelectedDoc(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>{t('documents.documentsView.modalTitle')}</h3>
              <span className={styles.modalHelp}>
                <DialogHelp context="document-review" title={t('documents.documentsView.modalTitle')} />
              </span>
              <button className={styles.modalClose} onClick={() => setSelectedDoc(null)}>
                ✕
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalContentSplit}>
                <div className={styles.modalLeftColumn}>
                  <div className={styles.previewContainer}>
                    {isImage ? (
                      <img
                        src={selectedDoc.downloadUrl}
                        alt={t('documents.documentsView.previewAlt')}
                        className={styles.previewImage}
                      />
                    ) : isPdf ? (
                      <iframe
                        src={selectedDoc.downloadUrl}
                        title={t('documents.documentsView.previewPdfTitle')}
                        className={styles.previewFrame}
                      />
                    ) : (
                      <iframe
                        src={selectedDoc.downloadUrl}
                        title={t('documents.documentsView.previewAlt')}
                        className={styles.previewFrame}
                      />
                    )}
                  </div>
                </div>

                <div className={styles.modalRightColumn}>
                  <div className={styles.docInfoGrid}>
                    <span className={styles.infoLabel}>{t('documents.documentsView.documentId')}</span>
                    <span className={styles.infoVal}>#{selectedDoc.id}</span>
                    {selectedDoc.shipmentId != null ? (
                      <>
                        <span className={styles.infoLabel}>{t('documents.documentsView.shipmentId')}</span>
                        <span className={styles.infoVal}>#{selectedDoc.shipmentId}</span>
                      </>
                    ) : (
                      <>
                        <span className={styles.infoLabel}>{t('documents.documentsView.vehicle')}</span>
                        <span className={styles.infoVal}>
                          {selectedDoc.registrationNumber ??
                            (selectedDoc.vehicleId != null ? `#${selectedDoc.vehicleId}` : '—')}
                        </span>
                      </>
                    )}
                    <span className={styles.infoLabel}>{t('documents.documentsView.docType')}</span>
                    <span className={styles.infoVal}>{fmtDocType(selectedDoc.type)}</span>
                    <span className={styles.infoLabel}>{t('documents.documentsView.size')}</span>
                    <span className={styles.infoVal}>{fmtSize(selectedDoc.fileSize)}</span>
                    <span className={styles.infoLabel}>{t('documents.documentsView.uploadedBy')}</span>
                    <span className={styles.infoVal}>{selectedDoc.uploadedByName || '—'}</span>
                  </div>

                  <a
                    href={selectedDoc.downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.previewLink}
                  >
                    {t('documents.documentsView.openInNewTab')}
                  </a>

                  <div className={styles.modalActions}>
                    <button className={styles.closeBtn} onClick={() => setSelectedDoc(null)}>
                      {t('common.actions.cancel')}
                    </button>
                    <button className={styles.rejectBtn} onClick={() => setIsRejectOpen(true)}>
                      {t('documents.documentsView.rejectDocument')}
                    </button>
                    <button className={styles.verifyBtn} onClick={() => setIsApproveOpen(true)}>
                      {t('documents.documentsView.verifyDocument')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Verify Confirm Dialog */}
      <ConfirmDialog
        help="document-review"
        isOpen={isApproveOpen}
        title={t('documents.documentsView.verifyTitle')}
        description={
          selectedDoc && selectedDoc.shipmentId == null
            ? `Are you sure you want to verify compliance document #${selectedDoc.id}? This marks the vehicle's ${VEHICLE_DOC_NOUN[selectedDoc.type] ?? 'document'} as verified.`
            : `Are you sure you want to verify compliance document #${selectedDoc?.id}? This will approve this document and allow the shipment to advance.`
        }
        confirmText="Verify"
        cancelText="Cancel"
        variant="success"
        loading={verifyDocMutation.isLoading}
        errorMessage={dialogError}
        onConfirm={() => {
          setDialogError(null);
          verifyDocMutation.mutate({
            documentId: selectedDoc!.id,
            approve: true,
          });
        }}
        onCancel={() => {
          setDialogError(null);
          setIsApproveOpen(false);
        }}
      />

      {/* Reject Confirm Dialog */}
      <ConfirmDialog
        help="document-review"
        isOpen={isRejectOpen}
        title={t('documents.documentsView.rejectTitle')}
        description={`Please enter a reason for rejecting compliance document #${selectedDoc?.id}. The uploader will see this reason and be required to re-upload.`}
        confirmText="Reject"
        cancelText="Cancel"
        variant="danger"
        requireReason={true}
        reasonPlaceholder="Enter rejection reason..."
        loading={verifyDocMutation.isLoading}
        errorMessage={dialogError}
        onConfirm={(reason) => {
          setDialogError(null);
          verifyDocMutation.mutate({
            documentId: selectedDoc!.id,
            approve: false,
            reason,
          });
        }}
        onCancel={() => {
          setDialogError(null);
          setIsRejectOpen(false);
        }}
      />
    </div>
  );
}
